import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from '@reduxjs/toolkit';
import { AppDispatch } from "@/setup/redux/Store";
import { getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';

// API functions
import {
  fetchOrderStatuses,
  fetchAdditionalFees,
  fetchShippingFees,
  fetchUnits,
  fetchSalesChannels,
  fetchCompanies,
} from "../utils/apiHelpers";
import { getProductTypeTax, ProductTypeTaxItem } from '@/modules/settings/redux/SettingsCRUD';
import type { OrderFormData, OrderItemBase } from "../models/OrderModel";
import { Company } from "@/modules/organizations/models/CompanyModel";
import { normalizeAddressFieldValue } from "@/modules/addresses/utils/normalizeAddressFieldValue";

// Timing constants
const INITIAL_FORM_DATA_TIMEOUT = 2500; // Time to wait before setting initial form data (allowing auto-fill to complete)
const INITIAL_DATA_SETTING_DELAY = 100; // Delay before allowing change detection after setting initial data
const ORDER_STATUSES_CACHE_VERSION = 2;

const getStatusDisplayName = (status: any): string => (
  status?.display_name ||
  status?.label ||
  status?.status_name ||
  status?.name ||
  ''
).toString().trim();

const normalizeOrderStatusOption = (status: any) => {
  const id = String(status?.id || status?.value || '');
  const name = getStatusDisplayName(status);

  if (!id || !name) {
    return null;
  }

  return {
    id,
    name,
    color: status.color || undefined,
    is_record_sales: Boolean(status.is_record_sales),
    is_default: Boolean(status.is_default),
    is_active: status.is_active !== false,
    category: status.category ? {
      id: status.category.id,
      name: status.category.name,
      code: status.category.code || '',
    } : undefined,
  };
};

const isOrderStatusOption = (
  status: ReturnType<typeof normalizeOrderStatusOption>
): status is NonNullable<ReturnType<typeof normalizeOrderStatusOption>> =>
  Boolean(status?.id && status?.name && status?.is_active);

// Memoized selectors to prevent unnecessary rerenders
const selectCustomersData = createSelector(
  [(state: any) => state.customer],
  (customersState) => ({
    customers: customersState?.customers || [],
    loading: customersState?.loading || false
  })
);

const selectEmployeesData = createSelector(
  [(state: any) => state.employees],
  (employeesState) => ({
    employees: employeesState?.employees || [],
    loading: employeesState?.loading || false
  })
);


export interface OrderItem extends OrderItemBase {
  id: number;
  item_type_tax: string;
  color: string;
  unit?: {
    id: string;
    name: string;
  } | null;
  unit_id: string;
  unit_name: string;
  total_price_not_tax: number;
}

interface UseOrderFormProps {
  order?: any;
  isEditMode?: boolean;
}

export const useOrderForm = ({ order, isEditMode = false }: UseOrderFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  // Use current user from auth state instead of fetching again
  const currentUser = useSelector((state: any) => state.auth.user);
  const selectedCompany = useSelector((state: any) => state.auth.selectedCompany);
  const orderLoadedRef = useRef<string | null>(null);
  const orderLoadedRefCompany = useRef<string | null>(null);
  const orderLoadedRefItemsCount = useRef<number>(-1);
  const orderLoadedRefGroupsCount = useRef<number>(-1);
  const orderLoadedRefWalletSignature = useRef<string | null>(null);
  const orderStatusesLoadedRef = useRef(false);
  const additionalFeesLoadedRef = useRef(false);
  const shippingFeesLoadedRef = useRef(false);
  const unitsLoadedRef = useRef(false);
  const salesChannelsLoadedRef = useRef(false);
  const walletsLoadedRef = useRef(false);
  const walletsLoadedCompanyKeyRef = useRef<string | null>(null);
  const companiesLoadedRef = useRef(false);

  // Redux selectors
  const { customers: reduxCustomers, loading: customersLoading } = useSelector(selectCustomersData);
  const { employees: reduxEmployees, loading: employeesLoading } = useSelector(selectEmployeesData);
  
  // Check if user has global role
  const hasGlobalRole = selectedCompany?.roles?.some((role: any) => 
    role.role_name === 'global' || 
    role.role_level === 'global' || 
    role.role_name === 'global_admin'
  );

  // Form state
  const [formData, setFormData] = useState<OrderFormData>({
    customer_id: "",
    sales_channel_id: "",
    sales_employee_id: "",
    company_id: "",
    status_id: "",
    wallet_id: "",
    sale_date: null,
    delivery_date: new Date(),
    notes: "",
    additional_fee_ids: [],
    shipping_installation_fee_ids: [],
    order_code: "",
    order_code_prefix: "",
    order_code_suffix: "",
    order_type: "main",
    order_code_parent: "",
    parent_order_id: "",
    parent_order_info: null,
    delivery_phone: "",
    delivery_address_obj: "",
    survey_date: null,
    survey_appointment_date: null,
    export_file_date: null,
    survey_date_text: "",
    survey_appointment_date_text: "",
    export_file_date_text: "",
    survey_notes: "",
    survey_fee_amount: 0,
    shipping_installation_fee_amount: 0,
  });

  const [orderItems, setOrderItems] = useState<OrderItem[]>([
    {
      id: 1,
      product_name: "",
      item_type_tax: "",
      color: "",
      quantity: 0,
      unit: null,
      unit_id: "",
      unit_name: "",
      unit_price: 0,
      total_price_not_tax: 0,
      total_price: 0,
    },
  ]);

  // Track initial data for change detection
  const [initialFormData, setInitialFormData] = useState<OrderFormData | null>(null);
  const [initialOrderItems, setInitialOrderItems] = useState<OrderItem[] | null>(null);
  const isSettingInitialDataRef = useRef(false); // Flag to track when we're setting initial data
  const companyIdLoadedRef = useRef(false); // Track when company_id has been loaded from order
  const autoDefaultStatusIdRef = useRef<string>("");
  const autoDefaultSalesChannelIdRef = useRef<string>("");
  const baselineOrderIdRef = useRef<string | null>(null);

  // Data states
  const [units, setUnits] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [salesChannels, setSalesChannels] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [salesChannelsLoading, setSalesChannelsLoading] = useState(false);
  const [wallets, setWallets] = useState<Array<{ id: string; name: string; code: string; is_default?: boolean }>>([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [, setCompaniesLoading] = useState(false);
  const [orderStatuses, setOrderStatuses] = useState<Array<{
    id: string;
    name: string;
    is_record_sales?: boolean;
    is_default?: boolean;
    is_active?: boolean;
    category?: { id: string; name: string; code: string };
  }>>([]);
  const [orderStatusesLoading, setOrderStatusesLoading] = useState(false);
  const [additionalFees, setAdditionalFees] = useState<Array<{ id: string; name: string; amount: number }>>([]);
  const [additionalFeesLoading, setAdditionalFeesLoading] = useState(false);
  const [shippingFees, setShippingFees] = useState<Array<{ id: string; name: string; amount: number }>>([]);
  const [shippingFeesLoading, setShippingFeesLoading] = useState(false);
  const [productTypes, setProductTypes] = useState<ProductTypeTaxItem[]>([]);
  const productTypesLoadedRef = useRef(false);

  // Processed data
  const customers = reduxCustomers.map((customer: any) => ({
    id: customer.id,
    name: customer.name,
    email: customer.email || customer.contact_email || `${customer.name.toLowerCase().replace(/\s+/g, "")}@email.com`,
    phone: customer.phone || customer.contact_phone || "",
    address: customer.address?.address_line || customer.address_display || "",
    address_obj: customer.address_obj_response || customer.address_obj || customer.address || {
      address_line: customer.address?.address_line || customer.address_display || "",
      province: customer.address?.province || "",
      ward: customer.address?.ward || "",
      free_address: customer.address?.free_address || "",
    },
  }));


  const employees = reduxEmployees;
  const currentEmployee = employees.find((emp: any) => emp.user?.id === currentUser?.id);

  const getOrderWalletOptions = useCallback((sourceOrder: any) => {
    if (!sourceOrder) return [];

    const rawWallets = [
      sourceOrder.wallet,
      ...(Array.isArray(sourceOrder.wallets) ? sourceOrder.wallets : []),
    ];

    return rawWallets
      .map((walletEntry: any) => {
        const wallet = walletEntry?.wallet || walletEntry;
        if (!wallet?.id) return null;

        return {
          id: String(wallet.id),
          name: wallet.name || sourceOrder.wallet_name || String(wallet.id),
          code: wallet.code || '',
          is_default: Boolean(wallet.is_default),
        };
      })
      .filter(Boolean) as Array<{ id: string; name: string; code: string; is_default?: boolean }>;
  }, []);

  const mergeWalletOptions = useCallback((
    baseWallets: Array<{ id: string; name: string; code: string; is_default?: boolean }>,
    extraWallets: Array<{ id: string; name: string; code: string; is_default?: boolean }>
  ) => {
    const walletMap = new Map<string, { id: string; name: string; code: string; is_default?: boolean }>();

    [...baseWallets, ...extraWallets].forEach((wallet) => {
      if (!wallet?.id) return;
      walletMap.set(String(wallet.id), {
        ...wallet,
        id: String(wallet.id),
        code: wallet.code || '',
      });
    });

    return Array.from(walletMap.values());
  }, []);

  // Load Redux data (only once globally)
  useEffect(() => {
    const loadReduxData = async () => {
      const companyIdForApi =
        selectedCompany?.company_id && selectedCompany.company_id !== 'global'
          ? selectedCompany.company_id
          : undefined;

      if (reduxCustomers.length === 0 && !customersLoading && !(window as any).__reduxCustomersDataFetching) {
        (window as any).__reduxCustomersDataFetching = true;
        const { fetchCustomers } = await import("@/modules/customers/redux/CustomerRedux");
        try {
          await dispatch(
            fetchCustomers(companyIdForApi ? ({ company: companyIdForApi } as any) : undefined) as any
          );
        } finally {
          (window as any).__reduxCustomersDataFetching = false;
          (window as any).__reduxCustomersDataFetched = true;
        }
      }

      if (reduxEmployees.length === 0 && !employeesLoading && !(window as any).__reduxEmployeesDataFetching) {
        (window as any).__reduxEmployeesDataFetching = true;
        const { fetchEmployeesThunk } = await import("@/modules/employees/redux/EmployeeRedux");
        try {
          await dispatch(
            fetchEmployeesThunk(companyIdForApi ? ({ company: companyIdForApi } as any) : undefined) as any
          );
        } finally {
          (window as any).__reduxEmployeesDataFetching = false;
          (window as any).__reduxEmployeesDataFetched = true;
        }
      }
    };

    loadReduxData();
  }, [
    dispatch,
    reduxCustomers.length,
    customersLoading,
    reduxEmployees.length,
    employeesLoading,
    selectedCompany?.company_id,
  ]);

  // Load all static data in a single effect to reduce API calls
  useEffect(() => {
    const loadAllData = async () => {
      const promises = [];
      
      // Initialize global cache objects if they don't exist
      if (!(window as any).__orderStaticDataCache) {
        (window as any).__orderStaticDataCache = {};
      }
      
      // Use global flags to ensure static data is only fetched once
      // Check both local ref and global cache to avoid duplicate fetches
      // IMPORTANT: If cache was cleared (e.g., after adding new status), we need to fetch again
      const hasCachedData = (window as any).__orderStaticDataCache.orderStatuses && 
                           Array.isArray((window as any).__orderStaticDataCache.orderStatuses) &&
                           (window as any).__orderStaticDataCache.orderStatuses.length > 0 &&
                           (window as any).__orderStaticDataCache.orderStatusesVersion === ORDER_STATUSES_CACHE_VERSION;
      
      if (!orderStatusesLoadedRef.current || !hasCachedData) {
        // Check if global cache has valid data
        if (hasCachedData && orderStatusesLoadedRef.current) {
          // Use cached data
          const cachedStatuses = ((window as any).__orderStaticDataCache.orderStatuses || [])
            .map(normalizeOrderStatusOption)
            .filter(isOrderStatusOption);
          (window as any).__orderStaticDataCache.orderStatuses = cachedStatuses;
          setOrderStatuses(cachedStatuses);
          setOrderStatusesLoading(false);
          orderStatusesLoadedRef.current = true;
        } else if (!(window as any).__orderStatusesDataFetching) {
          // Fetch only if not already fetching
          (window as any).__orderStatusesDataFetching = true;
          orderStatusesLoadedRef.current = true;
          (window as any).__orderStatusesDataFetched = true;
          promises.push(
            fetchOrderStatuses({ force: true }).then(statuses => {
              // Map to format expected by TopOrder: { id, name, color?, category? }
              const formattedStatuses = statuses
                .map(normalizeOrderStatusOption)
                .filter(isOrderStatusOption);
              (window as any).__orderStaticDataCache.orderStatuses = formattedStatuses;
              (window as any).__orderStaticDataCache.orderStatusesVersion = ORDER_STATUSES_CACHE_VERSION;
              setOrderStatuses(formattedStatuses);
              setOrderStatusesLoading(false);
            }).finally(() => {
              (window as any).__orderStatusesDataFetching = false;
            })
          );
        }
      } else {
        // Already loaded and cache is valid, use cached data
        const cachedStatuses = ((window as any).__orderStaticDataCache.orderStatuses || [])
          .map(normalizeOrderStatusOption)
          .filter(isOrderStatusOption);
        (window as any).__orderStaticDataCache.orderStatuses = cachedStatuses;
        setOrderStatuses(cachedStatuses);
        setOrderStatusesLoading(false);
      }
      
      if (!additionalFeesLoadedRef.current) {
        if ((window as any).__orderStaticDataCache.additionalFees) {
          additionalFeesLoadedRef.current = true;
          setAdditionalFees((window as any).__orderStaticDataCache.additionalFees);
          setAdditionalFeesLoading(false);
        } else if (!(window as any).__orderAdditionalFeesDataFetching) {
          (window as any).__orderAdditionalFeesDataFetching = true;
          additionalFeesLoadedRef.current = true;
          (window as any).__orderAdditionalFeesDataFetched = true;
          promises.push(
            fetchAdditionalFees().then(fees => {
              (window as any).__orderStaticDataCache.additionalFees = fees;
              setAdditionalFees(fees);
              setAdditionalFeesLoading(false);
            }).finally(() => {
              (window as any).__orderAdditionalFeesDataFetching = false;
            })
          );
        }
      }
      
      if (!shippingFeesLoadedRef.current) {
        if ((window as any).__orderStaticDataCache.shippingFees) {
          shippingFeesLoadedRef.current = true;
          setShippingFees((window as any).__orderStaticDataCache.shippingFees);
          setShippingFeesLoading(false);
        } else if (!(window as any).__orderShippingFeesDataFetching) {
          (window as any).__orderShippingFeesDataFetching = true;
          shippingFeesLoadedRef.current = true;
          (window as any).__orderShippingFeesDataFetched = true;
          promises.push(
            fetchShippingFees().then(fees => {
              (window as any).__orderStaticDataCache.shippingFees = fees;
              setShippingFees(fees);
              setShippingFeesLoading(false);
            }).finally(() => {
              (window as any).__orderShippingFeesDataFetching = false;
            })
          );
        }
      }
      
      if (!unitsLoadedRef.current) {
        if ((window as any).__orderStaticDataCache.units) {
          unitsLoadedRef.current = true;
          setUnits((window as any).__orderStaticDataCache.units);
          setUnitsLoading(false);
        } else if (!(window as any).__orderUnitsDataFetching) {
          (window as any).__orderUnitsDataFetching = true;
          unitsLoadedRef.current = true;
          (window as any).__orderUnitsDataFetched = true;
          promises.push(
            fetchUnits().then(unitsData => {
              (window as any).__orderStaticDataCache.units = unitsData;
              setUnits(unitsData);
              setUnitsLoading(false);
            }).finally(() => {
              (window as any).__orderUnitsDataFetching = false;
            })
          );
        }
      }
      
      if (!salesChannelsLoadedRef.current) {
        if ((window as any).__orderStaticDataCache.salesChannels) {
          salesChannelsLoadedRef.current = true;
          setSalesChannels((window as any).__orderStaticDataCache.salesChannels);
          setSalesChannelsLoading(false);
        } else if (!(window as any).__orderSalesChannelsDataFetching) {
          (window as any).__orderSalesChannelsDataFetching = true;
          salesChannelsLoadedRef.current = true;
          (window as any).__orderSalesChannelsDataFetched = true;
          promises.push(
            fetchSalesChannels().then(channels => {
              (window as any).__orderStaticDataCache.salesChannels = channels;
              setSalesChannels(channels);
              setSalesChannelsLoading(false);
            }).finally(() => {
              (window as any).__orderSalesChannelsDataFetching = false;
            })
          );
        }
      }
      
      if (!companiesLoadedRef.current) {
        if ((window as any).__orderStaticDataCache.companies) {
          companiesLoadedRef.current = true;
          setCompanies((window as any).__orderStaticDataCache.companies);
          setCompaniesLoading(false);
        } else if (!(window as any).__orderCompaniesDataFetching) {
          (window as any).__orderCompaniesDataFetching = true;
          companiesLoadedRef.current = true;
          (window as any).__orderCompaniesDataFetched = true;
          promises.push(
            fetchCompanies().then(companiesData => {
              (window as any).__orderStaticDataCache.companies = companiesData;
              setCompanies(companiesData);
              setCompaniesLoading(false);
            }).finally(() => {
              (window as any).__orderCompaniesDataFetching = false;
            })
          );
        }
      }
      
      // Fetch productTypes
      if (!productTypesLoadedRef.current) {
        if ((window as any).__orderStaticDataCache.productTypes) {
          productTypesLoadedRef.current = true;
          setProductTypes((window as any).__orderStaticDataCache.productTypes);
        } else if (!(window as any).__orderProductTypesDataFetching) {
          (window as any).__orderProductTypesDataFetching = true;
          productTypesLoadedRef.current = true;
          promises.push(
            getProductTypeTax().then(data => {
              const active = data.filter(pt => pt.is_active === true);
              (window as any).__orderStaticDataCache.productTypes = active;
              setProductTypes(active);
            }).finally(() => {
              (window as any).__orderProductTypesDataFetching = false;
            })
          );
        }
      }

      // Execute all API calls in parallel
      if (promises.length > 0) {
        await Promise.all(promises);
      }
    };
    
    loadAllData();
  }, []);

  const getNormalizedCompanyIdForWallets = useCallback((value: unknown): string => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized || normalized === 'all' || normalized === 'global') return '';
    return normalized;
  }, []);

  const walletsCompanyId = useMemo(() => {
    const orderCompanyId = order?.company_id || order?.company?.id || '';
    const storageCompanyId = getSelectedCompany()?.company_id || '';
    const selectedCompanyId = selectedCompany?.company_id || '';
    const formCompanyId = formData.company_id || '';

    return getNormalizedCompanyIdForWallets(
      isEditMode
        ? orderCompanyId
        : formCompanyId || storageCompanyId || selectedCompanyId,
    );
  }, [
    isEditMode,
    order?.company_id,
    order?.company?.id,
    formData.company_id,
    selectedCompany?.company_id,
    getNormalizedCompanyIdForWallets,
  ]);

  // Load wallets when company context is available (same pattern as NewTransaction)
  useEffect(() => {
    const loadWallets = async () => {
      if (!(window as any).__orderStaticDataCache) {
        (window as any).__orderStaticDataCache = {};
      }

      const walletsCacheKey = walletsCompanyId || 'all';
      const walletsCacheProp = `wallets_${walletsCacheKey}`;
      const cache = (window as any).__orderStaticDataCache;
      const shouldLoadWallets =
        !walletsLoadedRef.current || walletsLoadedCompanyKeyRef.current !== walletsCacheKey;

      if (!shouldLoadWallets) {
        if (cache?.[walletsCacheProp]) {
          setWallets(mergeWalletOptions(cache[walletsCacheProp], getOrderWalletOptions(order)));
        }
        return;
      }

      if (cache?.[walletsCacheProp]) {
        walletsLoadedRef.current = true;
        walletsLoadedCompanyKeyRef.current = walletsCacheKey;
        setWallets(mergeWalletOptions(cache[walletsCacheProp], getOrderWalletOptions(order)));
        setWalletsLoading(false);
        return;
      }

      const fetchingFlagKey = `__orderWalletsDataFetching_${walletsCacheKey}`;
      if ((window as any)[fetchingFlagKey]) {
        return;
      }

      (window as any)[fetchingFlagKey] = true;
      walletsLoadedRef.current = true;
      walletsLoadedCompanyKeyRef.current = walletsCacheKey;
      setWalletsLoading(true);

      try {
        const { fetchWalletsThunk } = await import('@/modules/settings/wallet/redux/WalletRedux');

        const fetchParams = walletsCompanyId
          ? { company: walletsCompanyId, company_id: walletsCompanyId, no_pagination: true }
          : { no_pagination: true };

        const result = await dispatch(fetchWalletsThunk(fetchParams as any));

        if (fetchWalletsThunk.fulfilled.match(result)) {
          const raw = result.payload;
          const walletsRaw = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.results)
              ? raw.results
              : [];

          const scopedWallets = walletsCompanyId
            ? walletsRaw.filter((wallet: any) => {
                const walletCompanyRaw = wallet?.company?.id || wallet?.company || wallet?.company_id || '';
                if (!walletCompanyRaw) return true;
                const walletCompanyId = String(walletCompanyRaw).trim();
                return walletCompanyId === walletsCompanyId;
              })
            : walletsRaw;

          const walletsData = scopedWallets.map((wallet: any) => ({
            id: wallet.id,
            name: wallet.name,
            code: wallet.code,
            is_default: Boolean(wallet.is_default),
          }));

          cache[walletsCacheProp] = walletsData;
          setWallets(mergeWalletOptions(walletsData, getOrderWalletOptions(order)));
        }
      } finally {
        setWalletsLoading(false);
        (window as any)[fetchingFlagKey] = false;
      }
    };

    loadWallets();
  }, [dispatch, getOrderWalletOptions, mergeWalletOptions, order, walletsCompanyId]);

  // Default wallet selection (create mode): prefer `is_default` from API
  useEffect(() => {
    if (isEditMode) return;
    if (!Array.isArray(wallets) || wallets.length === 0) return;

    const currentWalletId = String(formData.wallet_id || '').trim();
    const currentWalletValid =
      currentWalletId && wallets.some((wallet) => wallet.id === currentWalletId);
    if (currentWalletValid) return;

    const defaultWallet = wallets.find((wallet) => Boolean(wallet.is_default));
    if (!defaultWallet?.id) return;

    setFormData((prev) => ({ ...prev, wallet_id: defaultWallet.id }));
  }, [isEditMode, wallets, formData.wallet_id]);

  // Auto-select current employee
  useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (currentEmployee && !formData.sales_employee_id) {
      setFormData((prev) => ({
        ...prev,
        sales_employee_id: currentEmployee.id,
      }));
    }
  }, [isEditMode, currentEmployee, formData.sales_employee_id]);

  // Auto-select company from storage for non-global users when creating new order
  useEffect(() => {
    if (!isEditMode && !hasGlobalRole && selectedCompany && !formData.company_id) {
      // Get company ID from storage or selectedCompany
      const companyIdFromStorage = getSelectedCompany();
      const companyId = companyIdFromStorage?.company_id || selectedCompany.company_id;
      
      if (companyId && companyId !== 'global') {
        setFormData((prev) => ({
          ...prev,
          company_id: companyId,
        }));
      }
    }
  }, [isEditMode, hasGlobalRole, selectedCompany, formData.company_id]);

  // Default order status selection (create mode): prefer `is_default` from API.
  useEffect(() => {
    if (!isEditMode && orderStatuses.length > 0 && !formData.status_id) {
      const defaultStatus =
        orderStatuses.find((status) => Boolean(status.is_default) && status.id && status.name) ||
        orderStatuses.find((status) => status.name === 'Đơn hàng mới');
      
      if (defaultStatus) {
        autoDefaultStatusIdRef.current = defaultStatus.id;
        setFormData((prev) => ({
          ...prev,
          status_id: defaultStatus.id,
        }));
      }
    }
  }, [isEditMode, orderStatuses, formData.status_id]);

  // Auto-select default sales channel "Trực tiếp" for new orders
  useEffect(() => {
    if (!isEditMode && salesChannels.length > 0 && !formData.sales_channel_id) {
      const defaultChannel = salesChannels.find(channel => 
        channel.name === 'Trực tiếp' || channel.code === 'direct'
      );
      
      if (defaultChannel) {
        autoDefaultSalesChannelIdRef.current = defaultChannel.id;
        setFormData((prev) => ({
          ...prev,
          sales_channel_id: defaultChannel.id,
        }));
      }
    }
  }, [isEditMode, salesChannels, formData.sales_channel_id]);

  // Load order data when in edit mode
  useEffect(() => {
    // Reset orderLoadedRef if we're not in edit mode or if order changes
    if (!isEditMode || !order) {
      // Only reset if order ID has changed, to avoid losing reference when toggling edit mode
      if (!order || !order.id || order.id !== orderLoadedRef.current) {
        orderLoadedRef.current = null;
        orderLoadedRefCompany.current = null;
        orderLoadedRefWalletSignature.current = null;
        companyIdLoadedRef.current = false;
      }
      return;
    }
    
    // Wait for all required data to be loaded before mapping
    if (customers.length === 0 || salesChannels.length === 0 || companies.length === 0 || orderStatuses.length === 0) {
      // Don't reset orderLoadedRef - just wait for data to load
      return;
    }
    
    // Prevent infinite loop by checking if we already loaded this order
    // Check order ID/company and also payload richness (items/groups count)
    // so we still remap when detailed order data arrives after lightweight list payload.
    const currentCompanyId = order.company?.id || null;
    const previousCompanyId = orderLoadedRefCompany.current;
    const currentItemsCount = Array.isArray(order.items)
      ? order.items.length
      : (Array.isArray(order.items_data) ? order.items_data.length : 0);
    const currentGroupsCount = Array.isArray(order.groups)
      ? order.groups.length
      : (Array.isArray(order.groups_data) ? order.groups_data.length : 0);
    const currentWalletSignature = JSON.stringify({
      wallet_id: order.wallet_id || order.wallet?.id || '',
      wallet_name: order.wallet_name || order.wallet?.name || '',
      wallets: Array.isArray(order.wallets)
        ? order.wallets.map((wallet: any) => ({
            id: wallet?.id || wallet?.wallet?.id || '',
            name: wallet?.name || wallet?.wallet?.name || '',
          }))
        : [],
      available_wallets_count: wallets.length,
    });

    if (
      order.id === orderLoadedRef.current &&
      currentCompanyId === orderLoadedRefCompany.current &&
      currentItemsCount === orderLoadedRefItemsCount.current &&
      currentGroupsCount === orderLoadedRefGroupsCount.current &&
      currentWalletSignature === orderLoadedRefWalletSignature.current
    ) {
      return; // Already loaded this order with same company data
    }
    
    // Set orderLoadedRef before loading to prevent duplicate loads
    orderLoadedRef.current = order.id;
    orderLoadedRefCompany.current = currentCompanyId;
    orderLoadedRefItemsCount.current = currentItemsCount;
    orderLoadedRefGroupsCount.current = currentGroupsCount;
    orderLoadedRefWalletSignature.current = currentWalletSignature;
    baselineOrderIdRef.current = null;
    setInitialFormData(null);
    setInitialOrderItems(null);
    
    // Pass previousCompanyId to loadOrderData via closure
    
    const loadOrderData = async () => {
      const parseOptionalDate = (input: any): Date | null => {
        if (!input) return null;
        const value = input instanceof Date ? input : new Date(input);
        return Number.isNaN(value.getTime()) ? null : value;
      };

      const nestedOrder = (order as any)?.order || {};
      const techCncData = (order as any)?.tech_cnc || nestedOrder?.tech_cnc || {};

      // Convert dates from strings to Date objects
      const saleDate = order.sale_date ? new Date(order.sale_date) : null;
      const deliveryDate = order.delivery_date ? new Date(order.delivery_date) : new Date();
      const completionDate = order.completion_date ? new Date(order.completion_date) : null;
      
      // Handle delivery address mapping
      let deliveryAddressObj = { address_line: '', province: '', ward: '', free_address: '' };
      
      // If backend provides full address object, use it
      if (order.delivery_address && typeof order.delivery_address === 'object') {
        deliveryAddressObj = {
          address_line: order.delivery_address.address_line || order.delivery_address.full_address || '',
          province: order.delivery_address.province || '', // Use UUID directly
          ward: order.delivery_address.ward || '', // Use UUID directly
          free_address: order.delivery_address.free_address || order.delivery_address.address_line || ''
        };
      }
      // If delivery_address_obj is a string ID, we need to load the address from API
      else if (order.delivery_address_obj && typeof order.delivery_address_obj === 'string') {
        try {
          const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
          const response = await axiosInstance.get(`/addresses/${order.delivery_address_obj}/`);
          const addressData = response.data;
          deliveryAddressObj = {
            address_line: addressData.address_line || addressData.full_address || '',
            province: addressData.province || '', // Use UUID directly
            ward: addressData.ward || '', // Use UUID directly
            free_address: addressData.free_address || addressData.address_line || ''
          };
        } catch (error) {
          console.error('Error loading address:', error);
        }
      }
      // Fallback: check delivery_address_obj as object (for backward compatibility)
      else if (order.delivery_address_obj && typeof order.delivery_address_obj === 'object') {
        deliveryAddressObj = {
          address_line: order.delivery_address_obj.address_line || order.delivery_address_obj.full_address || '',
          province: order.delivery_address_obj.province || '', // Use UUID directly
          ward: order.delivery_address_obj.ward || '', // Use UUID directly
          free_address: order.delivery_address_obj.free_address || order.delivery_address_obj.address_line || ''
        };
      }
      
      // Helper function to find ID by name
      const findIdByName = (items: any[], name: string, nameField: string = 'name') => {
        if (!name || name === 'undefined' || !Array.isArray(items)) {
          return "";
        }
        const found = items.find(item => item[nameField] === name);
        return found?.id || "";
      };

      const companyNameToSearch = order.company_name || order.company?.name;
      const resolvedOrderCode = order.code || order.order_code || order.order?.code || order.order?.order_code || "";
      const resolvedOrderCodePrefix = order.order_code_prefix || order.order?.order_code_prefix || "";
      const resolvedOrderCodeSuffix = order.order_code_suffix || order.order?.order_code_suffix || "";
      
      // Debug logging for company_id resolution
      const resolvedCompanyId = order.company_id || order.company?.id || findIdByName(companies, companyNameToSearch) || "";
      
      const loadedFormData = {
        customer_id: order.customer_id || findIdByName(customers, order.customer_name) || "",
        sales_channel_id: order.sales_channel_id || order.sales_channel?.id || findIdByName(salesChannels, order.sales_channel_name) || "",
        sales_employee_id: order.sales_employee_id || order.sales_employee?.id || findIdByName(employees, order.sales_employee_name) || "",
        company_id: resolvedCompanyId,
        status_id: order.status_id || order.status?.id || findIdByName(orderStatuses, order.status_name) || "",
        wallet_id: (() => {
          const directId = order.wallet_id || order.wallet?.id;
          if (directId) return String(directId);
          
          if (Array.isArray(order.wallets) && order.wallets.length > 0) {
            const firstWallet = order.wallets[0];
            if (firstWallet?.id) return String(firstWallet.id);
            if (firstWallet?.wallet?.id) return String(firstWallet.wallet.id);
          }
          
          return findIdByName(wallets, order.wallet_name || order.wallet?.name) || "";
        })(),
        sale_date: saleDate,
        delivery_date: deliveryDate,
        completion_date: completionDate,
        notes: order.notes || "",
        additional_fee_ids: order.additional_fee_ids || [],
        shipping_installation_fee_ids: order.shipping_installation_fee_ids || [],
        order_code: resolvedOrderCode,
        order_code_prefix: resolvedOrderCodePrefix,
        order_code_suffix: resolvedOrderCodeSuffix,
        order_type: order.order_type || "main",
        order_code_parent: order.order_code_parent || "",
        parent_order_id: order.parent_order_id || order.parent_order?.id || "",
        parent_order_info: order.parent_order_info || null,
        delivery_phone: order.delivery_phone || "",
        delivery_address_obj: deliveryAddressObj,
        survey_id: order.survey?.employee || findIdByName(employees, order.survey_employee_name || order.survey?.employee_name) || "",
        survey_date: order.survey_date
          ? new Date(order.survey_date)
          : order.survey?.survey_date
            ? new Date(order.survey.survey_date)
            : null,
        survey_appointment_date: order.survey_appointment_date
          ? new Date(order.survey_appointment_date)
          : order.survey?.survey_appointment_date
            ? new Date(order.survey.survey_appointment_date)
            : order.survey?.appointment_date
              ? new Date(order.survey.appointment_date)
              : null,
        export_file_date: parseOptionalDate(
          (order as any).export_file_date ||
            nestedOrder?.export_file_date ||
            techCncData?.export_file_date
        ),
        export_file_date_text: (() => {
          const exportFileDate = parseOptionalDate(
            (order as any).export_file_date ||
              nestedOrder?.export_file_date ||
              techCncData?.export_file_date
          );
          if (!exportFileDate) return "";
          const dd = String(exportFileDate.getDate()).padStart(2, "0");
          const mm = String(exportFileDate.getMonth() + 1).padStart(2, "0");
          const yyyy = exportFileDate.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        })(),
        survey_notes:
          order.survey_notes ||
          order.survey?.survey_notes ||
          order.survey?.notes ||
          order.survey?.result ||
          "",
        tech_cnc_id:
          (order as any).tech_cnc_id ||
          techCncData?.employee ||
          techCncData?.employee_id ||
          findIdByName(
            employees,
            techCncData?.employee_name ||
              (order as any).tech_cnc_employee_name
          ) ||
          "",
        survey_fee_amount: order.survey_fee_amount || 0,
        shipping_installation_fee_amount: order.shipping_installation_fee_amount || 0,
      };

      setFormData((prev) => {
        // If this is a re-load (company changed but order ID is same), preserve auto-filled address
        // Check if company changed (was null, now has a value)
        const isOrderReLoadWithDifferentCompany = order.id === orderLoadedRef.current && currentCompanyId !== previousCompanyId;
        
        if (isOrderReLoadWithDifferentCompany) {
          // Check if prev has an auto-filled address
          const hasAutoFilledAddress = prev.delivery_address_obj && typeof prev.delivery_address_obj === 'object' &&
            (prev.delivery_address_obj.address_line || prev.delivery_address_obj.ward);

          const nextFormData: OrderFormData = {
            ...loadedFormData,
          };

          return nextFormData;
        }

        return loadedFormData;
      });

      // Baseline for change detection is captured after async UI normalization (see effect below).

      // Load order groups and items
      
      const newOrderItems: OrderItem[] = [];
      const resolveUnit = (item: any): {
        unit: { id: string; name: string } | null;
        unit_id: string;
        unit_name: string;
      } => {
        const unitId =
          typeof item.unit === 'string'
            ? item.unit
            : (item.unit?.id || item.unit_id || '');
        const unitName =
          item.unit_name ||
          (typeof item.unit === 'object' ? (item.unit?.name || '') : '');

        const normalizedUnitId = unitId ? String(unitId) : '';
        const normalizedUnitName = unitName || '';

        return {
          unit:
            normalizedUnitId || normalizedUnitName
              ? {
                  id: normalizedUnitId,
                  name: normalizedUnitName,
                }
              : null,
          unit_id: normalizedUnitId,
          unit_name: normalizedUnitName,
        };
      };
      
      // Sort groups by order_index
      const sortedGroups = order.groups && Array.isArray(order.groups) 
        ? [...order.groups].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        : [];
      
      // Sort items by order_index
      const sortedItems = order.items && Array.isArray(order.items)
        ? [...order.items].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        : [];
      
      // Create a map of groups for quick lookup
      const groupsMap = new Map();
      sortedGroups.forEach((group: any) => {
        groupsMap.set(group.id, group);
      });
      
      // Group items by their group_id
      const itemsByGroup = new Map();
      const itemsWithoutGroup: any[] = [];
      
      sortedItems.forEach((item: any) => {
        const groupId = item.group;
        if (groupId && groupsMap.has(groupId)) {
          if (!itemsByGroup.has(groupId)) {
            itemsByGroup.set(groupId, []);
          }
          itemsByGroup.get(groupId).push(item);
        } else {
          itemsWithoutGroup.push(item);
        }
      });
      
      let currentId = 1;
      
      // First, add items without group
      itemsWithoutGroup.forEach((item: any) => {
        const { unit, unit_id, unit_name } = resolveUnit(item);
        newOrderItems.push({
          id: currentId++,
          backend_id: item.id,
          product_name: item.product_name || "",
          item_type_tax: item.item_type_tax || "",
          color: item.color || "",
          quantity: item.quantity ?? 0,
          unit,
          unit_id,
          unit_name,
          // Ensure numbers are properly normalized
          unit_price: Number(typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : (item.unit_price || 0)),
          total_price_not_tax: Number(typeof item.total_price_not_tax === 'string' ? parseFloat(item.total_price_not_tax) : (item.total_price_not_tax || 0)),
          total_price: Number(typeof item.total_price === 'string' ? parseFloat(item.total_price) : (item.total_price || 0)),
          notes: item.notes || "",
          isGroup: false,
          groupName: "",
        });
      });
      
      // Then, add groups and their items
      sortedGroups.forEach((group: any) => {
        // Add group
        newOrderItems.push({
          id: currentId++,
          backend_id: group.id,
          product_name: "",
          item_type_tax: "",
          color: "",
          quantity: 0,
          unit_id: "",
          unit_name: "",
          unit_price: 0,
          total_price_not_tax: 0,
          total_price: 0,
          isGroup: true,
          groupName: group.name || "",
        });
        
        // Add items belonging to this group
        const groupItems = itemsByGroup.get(group.id) || [];
        groupItems.forEach((item: any) => {
          const { unit, unit_id, unit_name } = resolveUnit(item);
          newOrderItems.push({
            id: currentId++,
            backend_id: item.id,
            product_name: item.product_name || "",
            item_type_tax: item.item_type_tax || "",
            color: item.color || "",
            quantity: item.quantity ?? 0,
            unit,
            unit_id,
            unit_name,
            // Ensure numbers are properly normalized
            unit_price: Number(typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : (item.unit_price || 0)),
            total_price_not_tax: Number(typeof item.total_price_not_tax === 'string' ? parseFloat(item.total_price_not_tax) : (item.total_price_not_tax || 0)),
            total_price: Number(typeof item.total_price === 'string' ? parseFloat(item.total_price) : (item.total_price || 0)),
            notes: item.notes || "",
            isGroup: false,
            groupName: item.group_name || "",
          });
        });
      });
      
      // Fallback to items_data if groups and items are not available
      if (newOrderItems.length === 0 && order.items_data && Array.isArray(order.items_data)) {
        const items = order.items_data.map((item: any, index: number) => ({
          ...resolveUnit(item),
          id: index + 1,
          backend_id: item.id,
          product_name: item.product_name || "",
          item_type_tax: item.item_type_tax || "",
          color: item.color || "",
          quantity: item.quantity || 0,
          // Ensure numbers are properly normalized
          unit_price: Number(typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : (item.unit_price || 0)),
          total_price_not_tax: Number(typeof item.total_price_not_tax === 'string' ? parseFloat(item.total_price_not_tax) : (item.total_price_not_tax || 0)),
          total_price: Number(typeof item.total_price === 'string' ? parseFloat(item.total_price) : (item.total_price || 0)),
          notes: item.notes || "",
          isGroup: false,
          groupName: "",
        }));
        setOrderItems(items);
        // Save initial items for change detection
        setInitialOrderItems(JSON.parse(JSON.stringify(items)));
      } else {
        setOrderItems(newOrderItems);
        // Save initial items for change detection
        setInitialOrderItems(JSON.parse(JSON.stringify(newOrderItems)));
      }
    };
    
    loadOrderData();
  }, [
    order?.id,
    order?.company,
    order?.updated_at,
    Array.isArray(order?.items) ? order.items.length : 0,
    Array.isArray(order?.items_data) ? order.items_data.length : 0,
    Array.isArray(order?.groups) ? order.groups.length : 0,
    Array.isArray(order?.groups_data) ? order.groups_data.length : 0,
    isEditMode,
    customers.length,
    salesChannels.length,
    companies.length,
    orderStatuses.length,
    employees.length,
    wallets.length,
    order?.wallet_id,
    order?.wallet?.id,
    order?.wallet?.name,
    order?.wallet_name,
    Array.isArray(order?.wallets)
      ? order.wallets
          .map((wallet: any) => `${wallet?.id || wallet?.wallet?.id || ''}:${wallet?.name || wallet?.wallet?.name || ''}`)
          .join('|')
      : '',
  ]);

  const captureEditBaseline = useCallback(() => {
    isSettingInitialDataRef.current = true;

    setFormData((currentFormData) => {
      const normalizedAddress =
        currentFormData.delivery_address_obj &&
        typeof currentFormData.delivery_address_obj === 'object'
          ? normalizeAddressFieldValue(currentFormData.delivery_address_obj)
          : currentFormData.delivery_address_obj;

      const snapshot: OrderFormData = {
        ...currentFormData,
        delivery_address_obj: normalizedAddress,
      };

      setInitialFormData(JSON.parse(JSON.stringify(snapshot)));

      if (currentFormData.company_id) {
        companyIdLoadedRef.current = true;
      }

      return snapshot;
    });

    setInitialOrderItems(JSON.parse(JSON.stringify(orderItems)));

    setTimeout(() => {
      isSettingInitialDataRef.current = false;
    }, INITIAL_DATA_SETTING_DELAY);
  }, [orderItems]);

  // Capture edit baseline after order hydration + address/date field normalization
  useEffect(() => {
    if (!isEditMode || !order?.id || !formData.customer_id) {
      return;
    }

    if (
      customers.length === 0 ||
      salesChannels.length === 0 ||
      companies.length === 0 ||
      orderStatuses.length === 0
    ) {
      return;
    }

    if (baselineOrderIdRef.current === order.id) {
      return;
    }

    const timer = setTimeout(() => {
      baselineOrderIdRef.current = order.id;
      captureEditBaseline();
    }, INITIAL_FORM_DATA_TIMEOUT);

    return () => clearTimeout(timer);
  }, [
    isEditMode,
    order?.id,
    formData.customer_id,
    customers.length,
    salesChannels.length,
    companies.length,
    orderStatuses.length,
    captureEditBaseline,
  ]);
  

  // Form handlers
  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
        if (field === 'survey') {
          const survey = value || {};
          return {
            ...prev,
            survey_id: Object.prototype.hasOwnProperty.call(survey, 'survey_id')
              ? (survey.survey_id ?? "")
              : prev.survey_id,
            survey_date: Object.prototype.hasOwnProperty.call(survey, 'survey_date')
              ? (survey.survey_date ?? null)
              : prev.survey_date,
            survey_appointment_date: Object.prototype.hasOwnProperty.call(survey, 'survey_appointment_date')
              ? (survey.survey_appointment_date ?? null)
              : prev.survey_appointment_date,
            survey_date_text: Object.prototype.hasOwnProperty.call(survey, 'survey_date_text')
              ? (survey.survey_date_text ?? "")
              : prev.survey_date_text,
            survey_appointment_date_text: Object.prototype.hasOwnProperty.call(survey, 'survey_appointment_date_text')
              ? (survey.survey_appointment_date_text ?? "")
              : prev.survey_appointment_date_text,
            survey_notes: Object.prototype.hasOwnProperty.call(survey, 'survey_notes')
              ? (survey.survey_notes ?? "")
              : prev.survey_notes,
          };
        }

        // Special handling for delivery_address_obj
        if (field === 'delivery_address_obj') {
          // If value is null or undefined, allow the update
          if (!value) {
            return {
              ...prev,
              [field]: value,
            };
          }
          
          // If value is an object, check if it's different from current value
          if (typeof value === 'object' && prev.delivery_address_obj && typeof prev.delivery_address_obj === 'object') {
            const current = prev.delivery_address_obj as any;
            const isSame = 
              current.address_line === value.address_line &&
              current.province === value.province &&
              current.ward === value.ward &&
              current.free_address === value.free_address;
            
            if (isSame) {
              return prev; // Don't update if the address data is the same
            }
          }
          
          // Allow the update
          return {
            ...prev,
            [field]: value,
          };
        }
      
      const newData = {
        ...prev,
        [field as keyof OrderFormData]: value,
      };
      
      return newData;
    });
  };

  const resetForm = () => {
    autoDefaultStatusIdRef.current = "";
    autoDefaultSalesChannelIdRef.current = "";
    setFormData({
      customer_id: "",
      sales_channel_id: "",
      sales_employee_id: "",
      company_id: "",
      status_id: "",
      sale_date: null,
      delivery_date: new Date(),
      notes: "",
      additional_fee_ids: [],
      shipping_installation_fee_ids: [],
      order_code: "",
      order_code_prefix: "",
      order_code_suffix: "",
      order_type: "main",
      order_code_parent: "",
      parent_order_id: "",
      parent_order_info: null,
      delivery_phone: "",
      delivery_address_obj: "",
      survey_id: "",
      survey_date: null,
      survey_appointment_date: null,
      export_file_date: null,
      survey_date_text: "",
      survey_appointment_date_text: "",
      export_file_date_text: "",
      survey_notes: "",
      tech_cnc_id: "",
      survey_fee_amount: 0,
      shipping_installation_fee_amount: 0,
    });
    setOrderItems([
      {
        id: 1,
        product_name: "",
        item_type_tax: "",
        color: "",
        quantity: 0,
        unit: null,
        unit_id: "",
        unit_name: "",
        unit_price: 0,
        total_price_not_tax: 0,
        total_price: 0,
      },
    ]);
    // Reset initial data tracking
    baselineOrderIdRef.current = null;
    setInitialFormData(null);
    setInitialOrderItems(null);
  };

  const resetChangeTracking = () => {
    baselineOrderIdRef.current = order?.id ?? null;
    const normalizedAddress =
      formData.delivery_address_obj && typeof formData.delivery_address_obj === 'object'
        ? normalizeAddressFieldValue(formData.delivery_address_obj)
        : formData.delivery_address_obj;

    setInitialFormData(
      JSON.parse(
        JSON.stringify({
          ...formData,
          delivery_address_obj: normalizedAddress,
        })
      )
    );
    setInitialOrderItems(JSON.parse(JSON.stringify(orderItems)));
  };

  // Order items handlers
  const handleItemChange = (itemId: number, field: keyof OrderItem, value: any) => {
    setOrderItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };

          // Keep unit object and flat fields synchronized during refactor transition.
          if (field === "unit") {
            const unitValue = value as { id?: string; name?: string } | null;
            updatedItem.unit = unitValue
              ? { id: String(unitValue.id || ""), name: unitValue.name || "" }
              : null;
            updatedItem.unit_id = updatedItem.unit?.id || "";
            updatedItem.unit_name = updatedItem.unit?.name || "";
          } else if (field === "unit_id") {
            const nextUnitId = String(value || "");
            updatedItem.unit_id = nextUnitId;
            updatedItem.unit = nextUnitId || updatedItem.unit_name
              ? { id: nextUnitId, name: updatedItem.unit_name || "" }
              : null;
          } else if (field === "unit_name") {
            const nextUnitName = String(value || "");
            updatedItem.unit_name = nextUnitName;
            updatedItem.unit = updatedItem.unit_id || nextUnitName
              ? { id: updatedItem.unit_id || "", name: nextUnitName }
              : null;
          }

          // Tự động tính tổng tiền
          if (field === "quantity" || field === "unit_price") {
            const quantity = Number(updatedItem.quantity) || 0;
            const unitPrice = Number(updatedItem.unit_price) || 0;
            updatedItem.total_price = quantity * unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const addOrderItem = () => {
    const newItem: OrderItem = {
      id: orderItems.length + 1,
      product_name: "",
      item_type_tax: "",
      color: "",
      quantity: 0,
      unit: null,
      unit_id: "",
      unit_name: "",
      unit_price: 0,
      total_price_not_tax: 0,
      total_price: 0,
    };
    setOrderItems([...orderItems, newItem]);
  };

  const addGroupItem = () => {
    const newGroup: OrderItem = {
      id: orderItems.length + 1,
      product_name: "",
      item_type_tax: "",
      color: "",
      quantity: 0,
      unit: null,
      unit_id: "",
      unit_name: "",
      unit_price: 0,
      total_price_not_tax: 0,
      total_price: 0,
      isGroup: true,
      groupName: "",
    };
    setOrderItems([...orderItems, newGroup]);
  };

  const removeOrderItem = (itemId: number) => {
    setOrderItems(orderItems.filter((item) => item.id !== itemId));
  };

  // Data handlers
  const handleOrderStatusCreated = (newStatus: {
    id: string;
    name: string;
    category?: { id: string; name: string; code: string };
  }) => {
    setOrderStatuses((prev) => [...prev, newStatus]);
  };

  const refreshOrderStatuses = async () => {
    try {
      setOrderStatusesLoading(true);
      
      // Clear all flags and cache to force fresh fetch
      (window as any).__orderStatusesDataFetching = false;
      (window as any).__orderStatusesDataFetched = false;
      orderStatusesLoadedRef.current = false;
      
      // Clear cache to force fresh data
      if ((window as any).__orderStaticDataCache) {
        delete (window as any).__orderStaticDataCache.orderStatuses;
      }
      
      // Fetch fresh data
      const statuses = await fetchOrderStatuses({ force: true });
      
      // Map to format expected by TopOrder: { id, name, color?, category? }
      const formattedStatuses = statuses
        .map(normalizeOrderStatusOption)
        .filter(isOrderStatusOption);
      
      // Update cache with fresh data
      if ((window as any).__orderStaticDataCache) {
        (window as any).__orderStaticDataCache.orderStatuses = formattedStatuses;
        (window as any).__orderStaticDataCache.orderStatusesVersion = ORDER_STATUSES_CACHE_VERSION;
      }
      
      // Update state - this will trigger re-render
      // Create a completely new array to ensure React detects the change
      const newStatusesArray = formattedStatuses.map((s: any) => ({ ...s }));
      
      setOrderStatuses(newStatusesArray);
      orderStatusesLoadedRef.current = true;
      
      // Force a small delay to ensure state update is processed
      await new Promise(resolve => setTimeout(resolve, 200));
    } catch (error) {
      console.error('Error refreshing order statuses:', error);
    } finally {
      setOrderStatusesLoading(false);
    }
  };
  
  // Listen for global orderStatusesUpdated event to refresh when statuses are added/updated
  // This listener is placed after refreshOrderStatuses is defined
  useEffect(() => {
    const handleOrderStatusesUpdated = async () => {
      // Clear flags to force refresh
      orderStatusesLoadedRef.current = false;
      (window as any).__orderStatusesDataFetching = false;
      (window as any).__orderStatusesDataFetched = false;
      
      // Call refreshOrderStatuses to fetch fresh data
      await refreshOrderStatuses();
    };
    
    window.addEventListener('orderStatusesUpdated', handleOrderStatusesUpdated);
    
    return () => {
      window.removeEventListener('orderStatusesUpdated', handleOrderStatusesUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - we want this to run once and use the latest refreshOrderStatuses via closure

  const handleUnitCreated = (newUnit: { id: string; name: string; code: string }) => {
    setUnits((prev) => [...prev, newUnit]);
  };

  const handleSalesChannelCreated = (newChannel: { id: string; name: string; code: string }) => {
    setSalesChannels((prev) => [...prev, newChannel]);
  };

  const handleWalletCreated = (newWallet: { id: string; name: string; code: string; is_default?: boolean }) => {
    setWallets((prev) => [...prev, { ...newWallet, is_default: Boolean(newWallet.is_default) }]);
  };

  // Calculations
  const totalAmount = orderItems
    .filter((item) => !item.isGroup && item.total_price && !isNaN(Number(item.total_price)))
    .reduce((sum, item) => sum + (Number(item.total_price) || 0), 0);

  const additionalFeesTotal = additionalFees
    .filter((fee) => formData.additional_fee_ids.includes(fee.id) && fee.amount && !isNaN(Number(fee.amount)))
    .reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);

  const shippingFeesTotal = shippingFees
    .filter((fee) => formData.shipping_installation_fee_ids.includes(fee.id) && fee.amount && !isNaN(Number(fee.amount)))
    .reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);

  const grandTotal =
    (totalAmount || 0) +
    (Number(formData.survey_fee_amount) || 0) +
    (Number(formData.shipping_installation_fee_amount) || 0) +
    (additionalFeesTotal || 0) +
    (shippingFeesTotal || 0);

  // Expose initialFormDataLoaded flag for parent component
  const initialFormDataLoaded = !isEditMode || !!initialFormData;
  
  // Check if data has changes or if form is not empty (for new orders)
  const hasChanges = (() => {
    const normalizeDateInputText = (value: any): string =>
      String(value || "")
        .replace(/\u200e/g, "")
        .replace(/_/g, "")
        .replace(/\s+/g, " ")
        .trim();
    
    // For new orders, check if form has any meaningful data
    // Don't trigger unsaved warning for default values (dates, sales_employee_id, etc.)
    if (!isEditMode) {
      const defaultStatusId =
        orderStatuses.find((status) => Boolean(status.is_default) && status.id && status.name)?.id ||
        orderStatuses.find((status) => status.name === "Đơn hàng mới")?.id ||
        "";
      const defaultSalesChannelId =
        salesChannels.find(
          (channel) => channel.name === "Trực tiếp" || channel.code === "direct"
        )?.id || "";
      const effectiveDefaultStatusId = autoDefaultStatusIdRef.current || defaultStatusId;
      const effectiveDefaultSalesChannelId =
        autoDefaultSalesChannelIdRef.current || defaultSalesChannelId;

      // All these fields start with default/empty values, so any non-default value counts as user input
      const hasCustomerInput = !!formData.customer_id;
      const hasSalesChannelInput =
        !!formData.sales_channel_id && formData.sales_channel_id !== effectiveDefaultSalesChannelId;
      const hasStatusInput =
        !!formData.status_id && formData.status_id !== effectiveDefaultStatusId;
      const hasOrderCodeInput = !!formData.order_code;
      const hasDeliveryPhoneInput = !!formData.delivery_phone;
      // Ignore province/ward values that can be auto-populated by address APIs on first load.
      // Count as user input only when textual address fields are entered.
      const hasAddressInput =
        formData.delivery_address_obj &&
        typeof formData.delivery_address_obj === "object" &&
        (
          (formData.delivery_address_obj.address_line || "").trim() ||
          (formData.delivery_address_obj.free_address || "").trim()
        );
      const hasOrderItemsInput = orderItems.some(item => 
        !item.isGroup && 
        (item.product_name || item.quantity > 1 || item.unit_price > 0)
      );
      const hasNotesInput = !!formData.notes;
      const hasSurveyAppointmentDateTextInput = !!(formData.survey_appointment_date_text || '').trim();
      const hasSurveyDateTextInput = !!(formData.survey_date_text || '').trim();
      const hasExportFileDateTextInput = !!normalizeDateInputText(formData.export_file_date_text);
      const hasAdditionalFeesInput = formData.additional_fee_ids.length > 0;
      const hasShippingFeesInput = formData.shipping_installation_fee_ids.length > 0;
      const hasSurveyFeeInput = (formData.survey_fee_amount && formData.survey_fee_amount > 0);
      const hasShippingFeeAmountInput = (formData.shipping_installation_fee_amount && formData.shipping_installation_fee_amount > 0);
      
      // Return false if form is completely empty (no user input at all)
      const hasAnyChanges = !!(
        hasCustomerInput ||
        hasSalesChannelInput ||
        hasStatusInput ||
        hasOrderCodeInput ||
        hasDeliveryPhoneInput ||
        hasAddressInput ||
        hasOrderItemsInput ||
        hasNotesInput ||
        hasSurveyAppointmentDateTextInput ||
        hasSurveyDateTextInput ||
        hasExportFileDateTextInput ||
        hasAdditionalFeesInput ||
        hasShippingFeesInput ||
        hasSurveyFeeInput ||
        hasShippingFeeAmountInput
      );
      
      return hasAnyChanges;
    }
    
    // For edit mode, use existing comparison logic
    // Return false (button disabled) only when initial data is loaded AND no changes detected
    // If initial data is not yet loaded, return false (button disabled) as we can't determine changes yet
    if (!initialFormData || !initialOrderItems) {
      return false; // Disable button if initial data not loaded yet
    }
    
    // Helper function to compare dates
    const compareDates = (date1: Date | null | undefined, date2: Date | null | undefined): boolean => {
      // Handle null/undefined cases
      if (date1 == null && date2 == null) return true;
      if (date1 == null || date2 == null) return false;
      
      // Convert to Date objects if they are strings
      const d1 = date1 instanceof Date ? date1 : new Date(date1);
      const d2 = date2 instanceof Date ? date2 : new Date(date2);
      
      // Check if dates are valid
      if (isNaN(d1.getTime()) && isNaN(d2.getTime())) return true;
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;
      
      return d1.getTime() === d2.getTime();
    };

    const compareDateOnly = (date1: Date | null | undefined, date2: Date | null | undefined): boolean => {
      if (date1 == null && date2 == null) return true;
      if (date1 == null || date2 == null) return false;

      const d1 = date1 instanceof Date ? date1 : new Date(date1);
      const d2 = date2 instanceof Date ? date2 : new Date(date2);

      if (isNaN(d1.getTime()) && isNaN(d2.getTime())) return true;
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      );
    };

    // Helper function to compare arrays
    const compareArrays = (arr1: any[], arr2: any[]): boolean => {
      if (arr1.length !== arr2.length) return false;
      return arr1.every((item, index) => item === arr2[index]);
    };

    // Helper function to compare address objects
    const compareAddressObj = (addr1: any, addr2: any): boolean => {
      const normalizeIdLike = (value: any): string => {
        if (!value) return "";
        if (typeof value === "string") return value.trim();
        if (typeof value === "object") {
          if (typeof value.id === "string") return value.id.trim();
          if (typeof value.code === "string") return value.code.trim();
          if (typeof value.name === "string") return value.name.trim();
        }
        return String(value).trim();
      };

      const normalizeAddress = (addr: any) => {
        if (addr == null || addr === "") {
          return {
            address_line: "",
            province: "",
            ward: "",
            free_address: "",
          };
        }

        if (typeof addr === "string") {
          return {
            address_line: addr.trim(),
            province: "",
            ward: "",
            free_address: addr.trim(),
          };
        }

        if (typeof addr === "object") {
          return normalizeAddressFieldValue(addr);
        }

        const fallback = String(addr).trim();
        return {
          address_line: fallback,
          province: "",
          ward: "",
          free_address: fallback,
        };
      };

      const left = normalizeAddress(addr1);
      const right = normalizeAddress(addr2);

      return (
        left.address_line === right.address_line &&
        left.province === right.province &&
        left.ward === right.ward &&
        left.free_address === right.free_address
      );
    };

    // Compare formData
    // SPECIAL HANDLING: If initial company_id was empty but current is set from order, don't consider it a change
    // This handles the case where company data loads after initialFormData is set
    const companyChanged = (() => {
      const initialCompanyId = initialFormData.company_id;
      const currentCompanyId = formData.company_id;
      
      // If initial was empty and current is from order (not user input), it's not a real change
      // Only consider it a change if companyIdLoadedRef is true (meaning it was loaded after initialFormData)
      if (!initialCompanyId && currentCompanyId && order?.company_id === currentCompanyId && !companyIdLoadedRef.current) {
        return false; // Not a user change, just data loading
      }
      
      // Normal comparison
      return currentCompanyId !== initialCompanyId;
    })();
    
    const deliveryAddressChanged = !compareAddressObj(formData.delivery_address_obj, initialFormData.delivery_address_obj);
    
    const checks = {
      customer_id: formData.customer_id !== initialFormData.customer_id,
      sales_channel_id: formData.sales_channel_id !== initialFormData.sales_channel_id,
      sales_employee_id: formData.sales_employee_id !== initialFormData.sales_employee_id,
      company_id: companyChanged,
      status_id: formData.status_id !== initialFormData.status_id,
      wallet_id: formData.wallet_id !== initialFormData.wallet_id,
      order_code: formData.order_code !== initialFormData.order_code,
      order_code_prefix: formData.order_code_prefix !== initialFormData.order_code_prefix,
      sale_date: !compareDateOnly(formData.sale_date, initialFormData.sale_date),
      delivery_date: !compareDateOnly(formData.delivery_date, initialFormData.delivery_date),
      completion_date: !compareDateOnly(formData.completion_date || null, initialFormData.completion_date || null),
      notes: formData.notes !== initialFormData.notes,
      delivery_phone: formData.delivery_phone !== initialFormData.delivery_phone,
      delivery_address: deliveryAddressChanged,
      additional_fee_ids: !compareArrays(formData.additional_fee_ids, initialFormData.additional_fee_ids),
      shipping_installation_fee_ids: !compareArrays(formData.shipping_installation_fee_ids, initialFormData.shipping_installation_fee_ids),
      survey_id: formData.survey_id !== initialFormData.survey_id,
      survey_date: !compareDateOnly(formData.survey_date || null, initialFormData.survey_date || null),
      survey_date_text: false,
      survey_appointment_date: !compareDateOnly(
        formData.survey_appointment_date || null,
        initialFormData.survey_appointment_date || null
      ),
      survey_appointment_date_text: false,
      export_file_date: !compareDateOnly(formData.export_file_date || null, initialFormData.export_file_date || null),
      export_file_date_text: false,
      survey_notes: (formData.survey_notes || "") !== (initialFormData.survey_notes || ""),
      tech_cnc_id: formData.tech_cnc_id !== initialFormData.tech_cnc_id,
      survey_fee_amount: Number(formData.survey_fee_amount) !== Number(initialFormData.survey_fee_amount),
      shipping_installation_fee_amount: Number(formData.shipping_installation_fee_amount) !== Number(initialFormData.shipping_installation_fee_amount),
    };
    
    const formDataChanged = Object.values(checks).some(changed => changed);
    

    if (formDataChanged) {
      return true;
    }

    // Compare orderItems
    if (orderItems.length !== initialOrderItems.length) {
      return true;
    }
    
    const itemChecks = orderItems.map((item, index) => {
      const initialItem = initialOrderItems[index];
      const checks = {
        product_name: item.product_name !== initialItem.product_name,
        color: item.color !== initialItem.color,
        quantity: item.quantity !== initialItem.quantity,
        unit_id: item.unit_id !== initialItem.unit_id,
        unit_price: Number(item.unit_price) !== Number(initialItem.unit_price),
        item_type_tax: item.item_type_tax !== initialItem.item_type_tax,
        total_price_not_tax: Number(item.total_price_not_tax) !== Number(initialItem.total_price_not_tax),
        notes: (item.notes || "") !== (initialItem.notes || ""),
        // Don't compare total_price as it's auto-calculated and will always differ
        // total_price: Number(item.total_price) !== Number(initialItem.total_price),
        isGroup: item.isGroup !== initialItem.isGroup,
        groupName: item.groupName !== initialItem.groupName,
      };
      const changed = Object.values(checks).some(c => c);
      return { index, changed, item: item.product_name || 'Group: ' + item.groupName, checks };
    });
    
      const itemsChanged = itemChecks.some(check => check.changed);
      
      const result = formDataChanged || itemsChanged;
      
      // Don't consider it changed if initialFormData was just loaded or is being set
      // This prevents false positives from async data loading and initial setup
      if (result && ((isEditMode && !initialFormDataLoaded) || isSettingInitialDataRef.current)) {
        return false;
      }
      
      // SPECIAL CASE: Check if this is an auto-fill scenario
      // If delivery_address was empty and now has data from customer, it's auto-fill, not user input
      if (result && isEditMode && initialFormDataLoaded && !isSettingInitialDataRef.current) {
        const wasAddressEmpty = !initialFormData.delivery_address_obj || 
          (typeof initialFormData.delivery_address_obj === 'object' && 
           !initialFormData.delivery_address_obj.address_line && 
           !initialFormData.delivery_address_obj.province);
        
        const isAddressPopulated = formData.delivery_address_obj &&
          typeof formData.delivery_address_obj === 'object' &&
          (formData.delivery_address_obj.address_line || formData.delivery_address_obj.province);
        
        // Check if ONLY delivery_address changed (no other fields changed)
        const onlyAddressChanged = checks.delivery_address && 
          Object.entries(checks).filter(([key, val]) => key !== 'delivery_address').every(([_, val]) => !val);
        
        if (wasAddressEmpty && isAddressPopulated && onlyAddressChanged) {
          // This is auto-fill happening after initial load, refresh initialFormData
          isSettingInitialDataRef.current = true;
          setInitialFormData(() => JSON.parse(JSON.stringify(formData)));
          setInitialOrderItems(() => JSON.parse(JSON.stringify(orderItems)));
          setTimeout(() => {
            isSettingInitialDataRef.current = false;
          }, 100);
          
          return false; // Don't consider this a real change
        }
      }
      
      return result;
  })();

  return {
    // Form state
    formData,
    orderItems,
    setFormData,
    setOrderItems,
    
    // Data
    customers,
    employees,
    units,
    salesChannels,
    wallets,
    companies,
    orderStatuses,
    additionalFees,
    shippingFees,
    productTypes,
    
    // Loading states
    customersLoading,
    employeesLoading,
    unitsLoading,
    salesChannelsLoading,
    walletsLoading,
    orderStatusesLoading,
    additionalFeesLoading,
    shippingFeesLoading,
    
    // State flags
    initialFormDataLoaded,
    
    // Handlers
    handleInputChange,
    handleItemChange,
    addOrderItem,
    addGroupItem,
    removeOrderItem,
    resetForm,
    resetChangeTracking,
    handleOrderStatusCreated,
    refreshOrderStatuses,
    handleUnitCreated,
    handleSalesChannelCreated,
    handleWalletCreated,
    
    // Calculations
    totalAmount,
    additionalFeesTotal,
    shippingFeesTotal,
    grandTotal,
    
    // Change detection
    hasChanges,
  };
};
